import { useState } from "react";
import { Compass, LifeBuoy, Radio, Anchor, ShieldAlert, X } from "lucide-react";
import { CaptainRocaAI } from "./CaptainRocaAI";
import { useIsMobile } from "@/hooks/use-mobile";
import { CaptainLogo } from "./CaptainLogo";

type ActivePanel = "none" | "captain" | "snsm" | "crr" | "seashepherd" | "sos";

const actions = [
  {
    id: "captain" as const,
    icon: Compass,
    label: "Chat",
    color: "text-accent",
    activeBg: "bg-accent/15",
    activeBorder: "border-accent",
    activeGlow: "shadow-[0_0_16px_hsl(var(--accent)/0.4)]",
  },
  {
    id: "sos" as const,
    icon: ShieldAlert,
    label: "SOS",
    color: "text-destructive",
    activeBg: "bg-destructive/15",
    activeBorder: "border-destructive",
    activeGlow: "shadow-[0_0_16px_hsl(var(--destructive)/0.4)]",
  },
  {
    id: "snsm" as const,
    icon: LifeBuoy,
    label: "SNSM",
    color: "text-neon-cyan",
    activeBg: "bg-neon-cyan/15",
    activeBorder: "border-neon-cyan",
    activeGlow: "shadow-[0_0_16px_hsl(var(--neon-cyan)/0.4)]",
  },
  {
    id: "crr" as const,
    icon: Radio,
    label: "CRR",
    color: "text-electric-blue",
    activeBg: "bg-electric-blue/15",
    activeBorder: "border-electric-blue",
    activeGlow: "shadow-[0_0_16px_hsl(var(--electric-blue)/0.4)]",
  },
  {
    id: "seashepherd" as const,
    icon: Anchor,
    label: "Sea",
    color: "text-emerald-400",
    activeBg: "bg-emerald-400/15",
    activeBorder: "border-emerald-400",
    activeGlow: "shadow-[0_0_16px_rgba(52,211,153,0.4)]",
  },
];

export const BottomActionBar = () => {
  const [activePanel, setActivePanel] = useState<ActivePanel>("none");
  const isMobile = useIsMobile();

  const toggle = (id: ActivePanel) => setActivePanel(activePanel === id ? "none" : id);

  return (
    <>
      {/* ─── Bottom Bar ─── */}
      <div className={`fixed bottom-0 left-0 right-0 z-[60] bg-background/95 backdrop-blur-md border-t border-border/40 ${
        isMobile ? "" : "left-auto right-6 bottom-6 w-auto rounded-full border border-border/40 shadow-2xl"
      }`}>
        <div className={`flex items-center justify-around ${isMobile ? "px-2 py-1.5 max-w-md mx-auto" : "px-3 py-2 gap-1"}`}>
          {actions.map((a) => {
            const isActive = activePanel === a.id;
            return (
              <button
                key={a.id}
                onClick={() => toggle(a.id)}
                className={`flex flex-col items-center justify-center gap-0.5 rounded-xl transition-all duration-200 ${
                  isMobile ? "min-w-[56px] min-h-[56px] px-1 py-1" : "min-w-[48px] min-h-[48px] px-3 py-1.5"
                } ${isActive ? `${a.activeBg} ${a.activeBorder} border ${a.activeGlow}` : "border border-transparent hover:bg-secondary/50"}`}
                aria-label={a.label}
              >
                {a.id === "captain" ? (
                  <CaptainLogo size={isMobile ? 24 : 22} />
                ) : (
                  <a.icon className={`${a.color} ${isMobile ? "w-6 h-6" : "w-5 h-5"}`} />
                )}
                <span className={`text-[10px] font-medium leading-none ${isActive ? a.color : "text-muted-foreground"}`}>
                  {a.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* ─── Captain ROCA Full-Screen Chat ─── */}
      {activePanel === "captain" && (
        <div className="fixed inset-0 z-[70] bg-background/95 backdrop-blur-lg animate-fade-in">
          <button
            onClick={() => setActivePanel("none")}
            className="absolute top-4 right-4 z-10 p-2 rounded-full bg-secondary/50 hover:bg-secondary transition-colors"
          >
            <X size={24} className="text-foreground" />
          </button>
          <CaptainRocaAI onClose={() => setActivePanel("none")} />
        </div>
      )}

      {/* ─── SOS Dyslexia Panel ─── */}
      {activePanel === "sos" && (
        <PanelWrapper borderColor="border-destructive/60" onClose={() => setActivePanel("none")} isMobile={isMobile}>
          <div className="flex items-center gap-2 mb-3">
            <ShieldAlert className="w-5 h-5 text-destructive animate-pulse" />
            <span className="text-sm font-bold text-destructive uppercase tracking-wider">SOS Dyslexia</span>
          </div>
          <p className="text-xs text-muted-foreground mb-4">Emergency resources for dyslexic families</p>
          <div className="space-y-2">
            <PanelLink href="#dyslexia-sos" onClick={() => setActivePanel("none")} emoji="🚨" title="SOS Guide" desc="5-step diagnosis & school support" />
            <PanelLink href="https://www.dyslexiques-de-france.com" external emoji="🇫🇷" title="Dyslexiques de France" desc="Association nationale" />
            <PanelLink href="https://www.ffdys.com" external emoji="📚" title="FFDys" desc="Fédération Française des DYS" />
          </div>
        </PanelWrapper>
      )}

      {/* ─── SNSM Panel ─── */}
      {activePanel === "snsm" && (
        <PanelWrapper borderColor="border-neon-cyan/60" onClose={() => setActivePanel("none")} isMobile={isMobile}>
          <div className="flex items-center gap-2 mb-3">
            <LifeBuoy className="w-5 h-5 text-neon-cyan" />
            <span className="text-sm font-bold text-neon-cyan uppercase tracking-wider">SNSM</span>
          </div>
          <p className="text-xs text-muted-foreground mb-4">Société Nationale de Sauvetage en Mer</p>
          <div className="space-y-2">
            <PanelLink href="https://www.snsm.org" external emoji="🛟" title="SNSM Website" desc="Sea rescue & volunteer info" />
            <div className="p-3 rounded-lg bg-secondary/30">
              <p className="text-sm font-medium text-foreground">📻 VHF Channel 16</p>
              <p className="text-xs text-muted-foreground">156.8 MHz — Maritime distress</p>
            </div>
            <div className="p-3 rounded-lg bg-secondary/30">
              <p className="text-sm font-medium text-foreground">📞 196</p>
              <p className="text-xs text-muted-foreground">French maritime emergency</p>
            </div>
            <div className="p-3 rounded-lg bg-neon-cyan/10 border border-neon-cyan/20">
              <p className="text-xs text-neon-cyan font-medium">MAYDAY = immediate danger to life or vessel</p>
            </div>
          </div>
        </PanelWrapper>
      )}

      {/* ─── CRR / ASN Panel ─── */}
      {activePanel === "crr" && (
        <PanelWrapper borderColor="border-electric-blue/60" onClose={() => setActivePanel("none")} isMobile={isMobile}>
          <div className="flex items-center gap-2 mb-3">
            <Radio className="w-5 h-5 text-electric-blue" />
            <span className="text-sm font-bold text-electric-blue uppercase tracking-wider">CRR / ASN</span>
          </div>
          <p className="text-xs text-muted-foreground mb-4">Maritime radio training & DSC procedures</p>
          <div className="space-y-2">
            <PanelLink href="/crr-zone1" emoji="📡" title="Zone 1 — Coastal" desc="VHF & DSC basics" />
            <PanelLink href="/crr-zone2" emoji="📡" title="Zone 2 — MF" desc="Medium frequency" />
            <PanelLink href="/crr-zone3" emoji="📡" title="Zone 3 — HF/Sat" desc="Long range & satellite" />
            <PanelLink href="/crr-zone4" emoji="📡" title="Zone 4 — Polar" desc="Remote & polar areas" />
            <PanelLink href="/crr-flash" emoji="⚡" title="CRR Flash" desc="Quick revision cards" highlight />
          </div>
        </PanelWrapper>
      )}

      {/* ─── Sea Shepherd Panel ─── */}
      {activePanel === "seashepherd" && (
        <PanelWrapper borderColor="border-emerald-400/60" onClose={() => setActivePanel("none")} isMobile={isMobile}>
          <div className="flex items-center gap-2 mb-3">
            <Anchor className="w-5 h-5 text-emerald-400" />
            <span className="text-sm font-bold text-emerald-400 uppercase tracking-wider">Sea Shepherd</span>
          </div>
          <p className="text-xs text-muted-foreground mb-4">Ocean conservation & direct action</p>
          <div className="space-y-2">
            <PanelLink href="https://seashepherd.org" external emoji="🐬" title="Sea Shepherd Global" desc="Volunteer & campaigns" />
            <PanelLink href="https://www.seashepherd.fr" external emoji="🇫🇷" title="Sea Shepherd France" desc="French chapter" />
            <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-400/20">
              <p className="text-xs text-emerald-400 font-medium">🌊 "If the ocean dies, we die." — Captain Paul Watson</p>
            </div>
          </div>
        </PanelWrapper>
      )}
    </>
  );
};

/* ─── Shared panel wrapper ─── */
const PanelWrapper = ({
  children, borderColor, onClose, isMobile,
}: {
  children: React.ReactNode; borderColor: string; onClose: () => void; isMobile: boolean;
}) => (
  <div
    className={`fixed z-[65] ${
      isMobile ? "inset-x-3 bottom-[76px] max-h-[55vh] overflow-y-auto" : "bottom-[80px] right-6 w-[320px]"
    } bg-background/98 backdrop-blur-lg border-2 ${borderColor} rounded-2xl p-5 animate-fade-in shadow-2xl`}
  >
    <button onClick={onClose} className="absolute top-2 right-2 p-1.5 rounded-full hover:bg-secondary/50 transition-colors">
      <X size={16} className="text-muted-foreground" />
    </button>
    {children}
  </div>
);

/* ─── Shared panel link ─── */
const PanelLink = ({
  href, emoji, title, desc, external, onClick, highlight,
}: {
  href: string; emoji: string; title: string; desc: string; external?: boolean; onClick?: () => void; highlight?: boolean;
}) => (
  <a
    href={href}
    onClick={onClick}
    target={external ? "_blank" : undefined}
    rel={external ? "noopener" : undefined}
    className={`flex items-center p-3 rounded-lg transition-colors ${
      highlight
        ? "bg-electric-blue/10 border border-electric-blue/20 hover:bg-electric-blue/20"
        : "bg-secondary/30 hover:bg-secondary/50"
    }`}
  >
    <div>
      <p className="text-sm font-medium text-foreground">{emoji} {title}</p>
      <p className="text-xs text-muted-foreground">{desc}</p>
    </div>
  </a>
);
